import React, { Component } from "react";
import styles from "./style.module.scss";
import { Modal, Row, Spin } from "antd";
import CONST from "../../utilities/constant";
import { observable } from "mobx";
import { QUERY } from "../../utilities/network";
import { observer } from "mobx-react";
import DashboardOverView from "../../components/dashboardOverView";
import DashboardJourneyLevelChart from "../../components/dashboardJourneyLevelChart";
import DashboardEffectsChart from "../../components/dashboardEffectsChart";
import DashboardPracticedAreaChart from "../../components/dashboardPracticedAreaChart";
import DashboardTopBehaviorList from "../../components/dashboardTopBehaviorList";
import DashboardTrendChart from "../../components/dashboardTrendChart";
import DashboardHabitIndexTrend from "../../components/dashboardHabitIndexTrend";
import DashboardTrustNpsTrend from "../../components/dashboardTrustNpsTrend";

@observer
export default class AdvancedDashboard extends Component<{}> {
  @observable stats?: StatisticsType;
  @observable organizationName = "your";

  async componentDidMount() {
    await this._fetchDashboardData();
  }

  render() {
    if (!this.stats) {
      return (
        <div className={styles.vacant_container}>
          <Spin size="large" />
        </div>
      );
    }

    const {
      journey_sessions,
      effect_survey,
      weekly_trend,
      practiced_areas,
      practiced_loops,
      feed_forward_top_requested,
      feed_forward_top_satisfied,
      total_feed_forwards,
      feed_forward_quarterly_stats,
    } = this.stats;

    // @ts-ignore
    const loopIds = [...new Set(practiced_loops.map((e) => e.loop_id))];
    const uniquePracticedLoops = loopIds.map(
      (loop_id) => practiced_loops.find((e) => e.loop_id === loop_id) as any
    );
    return (
      <div className={styles.container}>
        <article>Welcome to your Leadership Dashboard!</article>

        <DashboardOverView statistics={this.stats} />

        <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
          <DashboardJourneyLevelChart journey_sessions={journey_sessions} />
          <DashboardEffectsChart effect_survey={effect_survey} />
        </Row>

        {weekly_trend.length > 0 && (
          <div className={styles.row_margin_top}>
            <DashboardTrendChart weekly_trend={weekly_trend} />
          </div>
        )}

        {feed_forward_quarterly_stats.length > 0 && (
          <div className={styles.row_margin_top}>
            <DashboardHabitIndexTrend
              feed_forward_quarterly_stats={feed_forward_quarterly_stats}
            />
          </div>
        )}
        {feed_forward_quarterly_stats.length > 0 && (
          <div className={styles.row_margin_top}>
            <DashboardTrustNpsTrend
              feed_forward_quarterly_stats={feed_forward_quarterly_stats}
            />
          </div>
        )}

        <div className={styles.row_margin_top}>
          <DashboardPracticedAreaChart
            practicedAreas={practiced_areas}
            practicedLoops={practiced_loops}
          />
        </div>

        <Row gutter={CONST.CONTENT_MARGIN} className={styles.row_margin_top}>
          <DashboardTopBehaviorList
            title="Top 5 habits"
            tips="The top 5 behaviors colleagues think their leaders do frequently enough. This is summarized from the Leadership Habit Profile, answered by the respective team member."
            loops={total_feed_forwards > 2 ? feed_forward_top_satisfied : []}
          />
          <DashboardTopBehaviorList
            title="Top 5 requested behaviors"
            tips="The top 5 behaviors colleagues wants leaders to do more of, more frequently. This is summarized from the Leadership Habit Profile, answered by the respective team member."
            loops={total_feed_forwards > 2 ? feed_forward_top_requested : []}
          />
          <DashboardTopBehaviorList
            title="Top practiced behaviors"
            tips="Top completed training sessions."
            loops={uniquePracticedLoops}
          />
        </Row>
      </div>
    );
  }

  _fetchDashboardData = async () => {
    try {
      const data = await QUERY<{
        statistics: StatisticsType;
        admin: { organization: { name: string } };
      }>(GET_STATS);
      this.stats = data.statistics;
      this.organizationName = data.admin.organization.name;
    } catch (e) {
      Modal.error({
        title: "Oops, something goes wrong.",
        content: e.message,
        okText: "GOT IT",
      });
    }
  };
}

const GET_STATS = `
query {
  admin: administratorInfo{
    organization {
      name
    }
  }
  statistics {
    group_ids
    active_groups
    total_members
    total_active_members
    total_inactive_members
    active_30_days_users
    total_training_activities
    total_training_sessions
    total_weekly_reflections
				total_feed_forwards
				completed_feed_forwards
    weekly_avg
    feed_forward_habit_index_avg
    feed_forward_last90days_habit_index_avg
    feed_forward_trust_nps
    feed_forward_last90days_trust_nps_avg
    feed_forward_last90days_total_records
    practiced_areas
    practiced_loops {
      loop_id
      loop_title
      loop_count
      area_id
      area_name
    }
    feed_forward_top_requested {
      loop_id
      loop_title
    }
    feed_forward_top_satisfied {
      loop_id
      loop_title
    }
    journey_sessions {
      count
      no_of_session
    }
    weekly_trend {
      year
      week
      training_sessions
      active_users
    }
    effect_survey {
      type
      no_of_yes
      no_of_no
    }
    feed_forward_quarterly_stats {
      year
      quarter
      total_lhp_records
      trust_nps_avg
      habit_index_avg
    }
  }
}`;

export interface StatisticsType {
  group_ids: Array<number>;
  active_groups: number;
  total_members: number;
  total_active_members: number;
  active_30_days_users: number;
  active_30_days_user_emails: Array<string>;
  total_training_activities: number;
  total_training_sessions: number;
  total_weekly_reflections: number;
  total_feed_forwards: number;
  completed_feed_forwards: number;
  weekly_avg: number;
  feed_forward_habit_index_avg: number | null;
  feed_forward_last90days_habit_index_avg: number | null;
  feed_forward_trust_nps: number | null;
  feed_forward_last90days_trust_nps_avg: number | null;
  feed_forward_last90days_total_records: number;
  practiced_areas: object;
  practiced_loops: Array<{
    loop_id: number;
    loop_title: string;
    loop_count: number;
    area_id: number;
    area_name: string;
  }>;
  journey_sessions: Array<{
    count: number;
    no_of_session: number;
  }>;
  feed_forward_top_requested: Array<{
    loop_id: number;
    loop_title: string;
  }>;
  feed_forward_top_satisfied: Array<{
    loop_id: number;
    loop_title: string;
  }>;
  effect_survey: Array<{
    type: "AwareDailyBehaviors" | "BecomeBetterLeaders" | "RecommendToOthers";
    no_of_yes: number;
    no_of_no: number;
  }>;
  weekly_trend: Array<{
    year: number;
    week: number;
    training_sessions: number;
    active_users: number;
  }>;
  feed_forward_quarterly_stats: Array<{
    year: number;
    quarter: 1 | 2 | 3 | 4;
    total_lhp_records: number;
    trust_nps_avg: number | null;
    habit_index_avg: number | null;
  }>;
}
